/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import { ResizeObserver } from '@oat-sa-private/ui-core';

/**
 * Read layout size from a ResizeObserver entry.
 * Prefer border/content box over getBoundingClientRect so ancestor CSS
 * transforms (device preview frame scale) do not shrink the reported width
 * used for responsive layout.
 * @param {ResizeObserverEntry} entry
 * @returns {{ width: number, height: number }}
 */
function getLayoutSize(entry) {
    const box = entry.borderBoxSize && entry.borderBoxSize[0];
    if (box) {
        return { width: box.inlineSize, height: box.blockSize };
    }
    if (entry.contentRect) {
        return { width: entry.contentRect.width, height: entry.contentRect.height };
    }
    const target = entry.target;
    return { width: target.clientWidth, height: target.clientHeight };
}

/**
 * Svelte Action to use ResizeObserver on node
 * (svelte's 'bind:clientWidth' doesn't work reliably when browser tab is blurred, and also attaches nested iframe)
 * @example
 * <div use:resizeObserve on:resized />
 * @param {HTMLElement} node - bind ResizeObserver to it
 * @param {Function} callback - 'resize' event handler function
 * @fires 'resized' - with {width, height} layout size in detail (ignores CSS transforms)
 * @returns {Object} - node's lifecycle hooks
 */
export default function resizeObserve(node, callback = () => {}) {
    let resizedEventHandlerCb = callback;
    let pendingDetail = null;
    let flushScheduled = false;
    let lastEmittedWidth;
    let lastEmittedHeight;

    function resizedEventHandler(evt) {
        resizedEventHandlerCb(evt);
    }

    /**
     * Coalesce rapid resize bursts and always emit the latest size.
     * @param {{ width: number, height: number }} detail
     */
    function scheduleFlush(detail) {
        pendingDetail = detail;
        if (flushScheduled) {
            return;
        }
        flushScheduled = true;
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                flushScheduled = false;
                const detailToEmit = pendingDetail;
                pendingDetail = null;
                if (!detailToEmit) {
                    return;
                }
                if (detailToEmit.width === lastEmittedWidth && detailToEmit.height === lastEmittedHeight) {
                    return;
                }
                lastEmittedWidth = detailToEmit.width;
                lastEmittedHeight = detailToEmit.height;
                node.dispatchEvent(
                    new CustomEvent('resized', {
                        detail: detailToEmit,
                        bubbles: false
                    })
                );
            });
        });
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
        scheduleFlush(getLayoutSize(entry));
    });

    resizeObserver.observe(node);
    node.addEventListener('resized', resizedEventHandler);

    return {
        update(newCallback) {
            if (typeof newCallback !== 'function') {
                resizedEventHandlerCb = () => {};
                return;
            }
            resizedEventHandlerCb = newCallback;
        },
        destroy() {
            resizeObserver.disconnect();
            node.removeEventListener('resized', resizedEventHandler);
            pendingDetail = null;
            flushScheduled = false;
        }
    };
}
